import {
  Drawer,
  IconButton,
  Button,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import '../../styles/dashboard.scss';

const GeorgianPhysicalPaymentDrawerComponent = ({
  isDrawerOpen,
  handleDrawerToggle,
  aRow,
  setARow,
}) => {

  const DrawerConfig = {
    SubNavigation: false,
    anchor: 'right',
    width: '40%',
    drawerHeaderText: 'Payment Details (Read Only)',
    SubNav: true,
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    zIndex: 2,
    top: 0,
    left: 0,
    display: isDrawerOpen ? 'block' : 'none',
    cursor: 'pointer',
  };

  const handleOutsideClick = (event) => {
    if (event.target === event.currentTarget) {
      setARow({});
      handleDrawerToggle();
    }
  };


  const getStatusColor = (status) => (status ? 'success' : 'warning');

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'USD':
        return '💵';
      case 'GEL':
        return '💸';
      default:
        return '💰';
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={containerStyle}
      onClick={handleOutsideClick}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setARow({});
          handleDrawerToggle();
        }
      }}
    >
      <Drawer
        variant="persistent"
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: { width: DrawerConfig.width },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid rgba(220, 220, 220, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{DrawerConfig.drawerHeaderText}</Typography>
            <Chip
              label="Read Only"
              color="info"
              size="small"
              variant="outlined"
            />
          </Box>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ padding: '24px', height: '100%', overflow: 'auto' }}>
          <Stack spacing={3}>
            {/* Payment Summary */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Summary
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Group ID:</Typography>
                    <Typography fontWeight="bold">#{aRow?.id || 'N/A'}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Tracking Numbers:</Typography>
                    <Typography fontWeight="bold">{aRow?.trackingNumbers || 'N/A'}</Typography>
                  </Box>
 
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Total Weight:</Typography>
                    <Typography fontWeight="bold">{aRow?.totalWeight || 0} kg</Typography>
                  </Box>

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total (USD):</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      ${aRow?.totalUSD || 0}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total (GEL):</Typography>
                    <Typography variant="h6" color="secondary" fontWeight="bold">
                      {aRow?.totalGel || 0} ₾
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Order List */}
            {aRow?.orders && aRow.orders.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Orders in this Group ({aRow.orders.length})
                  </Typography>
                  <Stack spacing={2}>
                    {aRow.orders.map((order, index) => (
                      <Box
                        key={order.id || index}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'background.neutral',
                        }}
                      >
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {order.name || `Order ${index + 1}`}
                            </Typography>
                            <Chip
                              label={order.status || 'Unknown'}
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Tracking Number:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {order.tracking_number || 'N/A'}
                            </Typography>
                          </Box>
                          
                          {order.tracking_number_2 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Tracking Number 2:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {order.tracking_number_2}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Weight:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {order.weight ? `${order.weight} kg` : 'N/A'}
                            </Typography>
                          </Box>
                          
                          {order.content && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Content:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {order.content}
                              </Typography>
                            </Box>
                          )}
                          
                          {order.additional_identifier && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Additional ID:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {order.additional_identifier}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Payment Details */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>
                <Stack spacing={2}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography>Payment Method:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>{getPaymentMethodIcon(aRow?.payed_by)}</Typography>
                      <Typography fontWeight="bold">{aRow?.payed_by || 'N/A'}</Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography>Payment Status:</Typography>
                    <Chip
                      label={aRow?.payed ? 'Paid' : 'Unpaid'}
                      color={getStatusColor(aRow?.payed)}
                      variant="filled"
                    />
                  </Box>

                  {aRow?.createdAt && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Created Date:</Typography>
                      <Typography fontWeight="bold">
                        {new Date(aRow.createdAt).toLocaleDateString('ka-GE')}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>


            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 'auto' }}>
              <Button variant="outlined" onClick={handleDrawerToggle}>
                Close
              </Button>
            </Box>
          </Stack>
        </Box>
      </Drawer>
    </div>
  );
};

GeorgianPhysicalPaymentDrawerComponent.propTypes = {
  isDrawerOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  aRow: PropTypes.object.isRequired,
  setARow: PropTypes.func.isRequired,
};

export default GeorgianPhysicalPaymentDrawerComponent;
